/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import React, { useEffect, useRef } from 'react';

import { deferScript } from '@confluence/dom-helpers';
import { expValEquals } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

// Get preloaded WRM resources from window.__WRM_RESOURCES__ if in SSR environment or page is SSR rendered
function getWrmResources(renderServerPlaceholder: boolean) {
	if (
		(renderServerPlaceholder || window?.__SSR_RENDERED__) &&
		window?.__WRM_RESOURCES__?.preloaded
	) {
		return window.__WRM_RESOURCES__ ?? null;
	}
	return null;
}

export type LegacyWrmRendererComponentProps = {
	wrmType: 'css' | 'js';
	renderComplete?: boolean;
};

const LegacyWrmRendererComponent: React.FC<LegacyWrmRendererComponentProps> = ({
	wrmType,
	renderComplete = false,
}) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const wrmResources = getWrmResources(renderServerPlaceholder);
	const wrmCss = wrmResources ? (wrmResources.css ?? '') : '';
	const wrmJs = wrmResources ? (wrmResources.js ?? '') : '';

	const renderCss = wrmType === 'css' && wrmCss;
	const renderJs = wrmType === 'js' && wrmJs;

	const jsContainerRef = useRef<HTMLDivElement>(null);

	// If renderComplete becomes true, rerender the JS container to ensure the JS is executed after the DOM is hydrated
	useEffect(() => {
		if (renderComplete && wrmType === 'js' && wrmJs && jsContainerRef.current) {
			jsContainerRef.current.innerHTML = deferScript(wrmJs);
		}
	}, [renderComplete, wrmJs, wrmType]);

	if (!renderCss && !renderJs) {
		return null;
	}

	return (
		<>
			{renderCss ? (
				<div
					data-testid="legacy_wrm_css"
					dangerouslySetInnerHTML={{ __html: wrmCss }}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ display: 'none' }}
				/>
			) : null}
			{renderJs ? (
				<div
					ref={jsContainerRef}
					data-testid="legacy_wrm_js"
					dangerouslySetInnerHTML={{ __html: deferScript(wrmJs) }}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ display: 'none' }}
				/>
			) : null}
		</>
	);
};

export type LegacyWrmRendererProps = LegacyWrmRendererComponentProps;

export const LegacyWrmRenderer: React.FC<LegacyWrmRendererProps> = (props) => {
	if (!expValEquals('confluence_preload_default_wrm_css', 'isEnabled', true)) {
		return null;
	}
	return <LegacyWrmRendererComponent {...props} />;
};
